import { useState, useRef, useCallback, useEffect } from "react";
import {
  Popper,
  Paper,
  Stack,
  Typography,
  TextField,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@material-ui/core";
import { Add, Delete, Group } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { fDateTimeSuffix } from "utils/formatTime";
import useSettings from "hooks/useSettings";
import { useOutsideDismiss } from "hooks/useOutsideDismiss";
import { useKeepAboveKeyboard } from "hooks/useKeepAboveKeyboard";
import type { UserNote } from "../../../@types/sharedSchema";

/**
 * Display-only metadata layered on top of `UserNote`. When `isMine === false`
 * the popover renders the note read-only with the author's name shown.
 * Hooks that don't fetch teammates' notes can keep returning plain
 * `UserNote`s — the missing fields are treated as `isMine: true`.
 */
/**
 * Mark a quick-notes TRIGGER with this so an open popover elsewhere hands it the tap instead
 * of eating it — see `keepClickForAnotherTrigger`. Spread it onto the button:
 * `{...QUICK_NOTES_TRIGGER_PROP}`.
 */
export const QUICK_NOTES_TRIGGER_ATTR = "data-quick-notes-trigger";
export const QUICK_NOTES_TRIGGER_PROP = { [QUICK_NOTES_TRIGGER_ATTR]: "" } as const;

export type PopoverNote = UserNote & {
  isMine?: boolean;
  authorName?: string;
};

type QuickNotesPopoverProps = {
  anchorEl: HTMLElement | null;
  notes: PopoverNote[];
  isLoading: boolean;
  onClose: () => void;
  onAddNote: (text: string) => void;
  onUpdateNote: (noteId: string, text: string) => void;
  onDeleteNote: (noteId: string) => void;
  /**
   * Optional per-note org-share toggle. When provided, each note renders a
   * "share with office" checkbox. Omit if the caller doesn't support
   * sharing (e.g. legacy callers); the checkbox is then hidden.
   */
  onSetNoteOrgShare?: (noteId: string, share: boolean) => void;
};

export function QuickNotesPopover({
  anchorEl,
  notes,
  isLoading,
  onClose,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  onSetNoteOrgShare
}: QuickNotesPopoverProps) {
  const { t } = useTranslation();
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";

  const [newNoteText, setNewNoteText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAddNote = useCallback(() => {
    if (!newNoteText.trim()) return;
    onAddNote(newNoteText);
    setNewNoteText("");
    inputRef.current?.focus();
  }, [newNoteText, onAddNote]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleAddNote();
      }
    },
    [handleAddNote]
  );

  // ── dismissal ───────────────────────────────────────────────────────────────

  /**
   * ARMED ONE TICK AFTER OPENING, like the CRM cell and chip editors. The click that opened
   * this popover is still propagating toward `document` when React commits the mount, and a
   * capture-phase listener added mid-propagation still receives it — so an immediately-live
   * one reads the opening click as a click away and closes the popover in the same gesture
   * that asked for it. Keyed on `anchorEl` rather than mount, because this component stays
   * mounted across open/close: the Popper is what comes and goes.
   */
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!anchorEl) {
      setArmed(false);
      return undefined;
    }
    const handle = setTimeout(() => setArmed(true), 0);
    return () => clearTimeout(handle);
  }, [anchorEl]);

  /**
   * THE PAPER **AND** THE ANCHOR COUNT AS INSIDE.
   *
   * The Paper because the surface is PORTALLED — testing the trigger's subtree would find
   * nothing, since the popover is not in it.
   *
   * The anchor because the trigger is a TOGGLE. This preserves, deliberately, the exemption
   * the old `ClickAwayListener` carried in its own comment: a pointerdown on the trigger
   * would otherwise dismiss here and the trigger's own `onClick` would immediately re-open,
   * so the button would appear not to close the popover at all.
   */
  const paperRef = useRef<HTMLDivElement | null>(null);
  const isInsideSurface = useCallback(
    (node: Node) =>
      Boolean(paperRef.current?.contains(node)) || Boolean(anchorEl?.contains(node)),
    [anchorEl]
  );

  /**
   * ⚠️ ONE OUTSIDE TAP IS NOT SWALLOWED: a tap on ANOTHER quick-notes trigger.
   *
   * Every row renders its own popover, so row B's button is "outside" row A's surface. Eat
   * that click and A closes while B's `onClick` never fires — moving between two rows' notes
   * costs TWO taps, where the `ClickAwayListener` this replaced cost one. The regression
   * would have been invisible here and shipped to all five surfaces that render this
   * popover, none of which was in the report's scope.
   *
   * Declining to swallow still DISMISSES; it only lets the other trigger open in the same
   * gesture. The marker is an attribute rather than a class or a ref because the triggers
   * are in different trees entirely — a CRM row, a listings-board card, a rental-to-sale row
   * — and none of them can see this component.
   */
  const keepClickForAnotherTrigger = useCallback(
    (target: Node) =>
      !(target instanceof Element) || !target.closest(`[${QUICK_NOTES_TRIGGER_ATTR}]`),
    []
  );

  /**
   * THE DISMISSING TAP CLOSES THE NOTES AND DOES NOTHING ELSE.
   *
   * Rejected on the mobile card, where the popover overlays the contact rows: *"on click
   * outside of the quick-notes input we want to capture that click, and not trigger what's
   * inside"* — the tap that put the notes away was also opening the contact underneath.
   * `useOutsideDismiss` runs on `pointerdown` in the capture phase and arms a one-shot
   * document-level click swallow that outlives this Popper's unmount, which is the only
   * place the swallow can live: by the time the `click` exists the surface is already gone.
   *
   * It fully replaces the `ClickAwayListener` this used to wrap the Paper in. That listener
   * was already reaching for the same early-in-the-cycle trick (`mouseEvent="onMouseDown"`,
   * `touchEvent="onTouchStart"`, for the same "surrounding rows call stopPropagation"
   * reason), and `pointerdown` is one event covering both — mouse, touch and pen. Keeping
   * both would mean two mechanisms closing one popover, which is how they come to disagree
   * about which one already handled the gesture.
   *
   * ⚠️ `onDismiss` IS `onClose` ALONE. Dismissing must not post the half-typed note sitting
   * in the box: an outside tap discards an unsent draft today, and quietly turning that into
   * a save is a behaviour nobody asked for. The hook's `blurFocusedControl` runs first and
   * is harmless for that draft — the new-note `TextField` lifts nothing on blur, only on
   * Enter or the ＋ button. It DOES now commit an in-progress edit of an existing note,
   * whose `TextField` saves from `onBlur`: previously that edit was lost, because removing a
   * focused element from the DOM fires no blur event at all. Saving it matches what every
   * other way of leaving that field already does.
   */
  useOutsideDismiss({
    enabled: armed && Boolean(anchorEl),
    isInside: isInsideSurface,
    onDismiss: onClose,
    shouldSwallow: keepClickForAnotherTrigger
  });

  /**
   * The soft keyboard covers the input this popover just auto-focused — the other half of
   * the same rejection.
   *
   * The SURFACE is tracked and the hook reveals whichever field inside it holds focus. The
   * two obvious alternatives are each wrong in one direction: tracking the new-note input
   * alone helps only the first field, while the one most likely to be under the keyboard is
   * an EXISTING note being edited at the bottom of the list; measuring the Paper itself
   * would reveal all 400px of a scrolled list and push the box being typed in off the TOP.
   */
  useKeepAboveKeyboard(paperRef, Boolean(anchorEl));

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300 }}
    >
      <Paper
        ref={paperRef}
        elevation={8}
        sx={{ width: 300, maxHeight: 400, overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Stack sx={{ p: 1.5 }} spacing={1}>
          <Typography variant="subtitle2">{t("rentalToSale.notes.title")}</Typography>

          {/* Add new note */}
          <Stack direction="row" gap={0.5} alignItems="flex-start">
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              multiline
              maxRows={3}
              placeholder={t("rentalToSale.notes.placeholder")}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <IconButton
              size="small"
              color="primary"
              onClick={handleAddNote}
              disabled={!newNoteText.trim()}
            >
              <Add fontSize="small" />
            </IconButton>
          </Stack>

          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {/* Existing notes */}
          {notes.length > 0 && <Divider />}
          {notes.map((note) => (
            <NoteItem
              key={note.id}
              note={note}
              isRTL={isRTL}
              onUpdate={onUpdateNote}
              onDelete={onDeleteNote}
              onSetOrgShare={onSetNoteOrgShare}
            />
          ))}

          {!isLoading && notes.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
              {t("rentalToSale.notes.empty")}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Popper>
  );
}

function NoteItem({
  note,
  isRTL,
  onUpdate,
  onDelete,
  onSetOrgShare
}: {
  note: PopoverNote;
  isRTL: boolean;
  onUpdate: (noteId: string, text: string) => void;
  onDelete: (noteId: string) => void;
  onSetOrgShare?: (noteId: string, share: boolean) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note.note);

  const handleBlur = useCallback(() => {
    setEditing(false);
    if (editText.trim() !== note.note) {
      if (editText.trim()) {
        onUpdate(note.id, editText);
      } else {
        onDelete(note.id);
      }
    }
  }, [editText, note, onUpdate, onDelete]);

  const isShared = (note.organizationIds?.length ?? 0) > 0;
  // Hooks that don't set `isMine` (legacy callers) are treated as
  // owning all notes — preserves existing behavior for contact notes
  // until they're upgraded to the dual-listener pattern too.
  const isMine = note.isMine ?? true;

  return (
    <Stack
      sx={{
        p: 1,
        borderRadius: 1,
        // Visually distinct background for teammates' notes so the user
        // can see at a glance which are theirs vs. shared by others.
        bgcolor: isMine ? "action.hover" : "info.lighter",
        "&:hover .note-delete": { opacity: 1 }
      }}
      spacing={0.5}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        {editing && isMine ? (
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={handleBlur}
            autoFocus
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              cursor: isMine ? "pointer" : "default",
              ...(isMine && {
                "&:hover": { bgcolor: "action.selected", borderRadius: 0.5 }
              })
            }}
            onClick={
              isMine
                ? () => {
                    setEditing(true);
                    setEditText(note.note);
                  }
                : undefined
            }
          >
            {note.note}
          </Typography>
        )}
        {!editing && isMine && (
          <IconButton
            className="note-delete"
            size="small"
            sx={{ opacity: 0, transition: "opacity 0.15s", ml: 0.5 }}
            onClick={() => onDelete(note.id)}
          >
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.5}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {fDateTimeSuffix(note.noteTime, isRTL)}
          </Typography>
          {/* Teammate-authored notes get an author chip so the reader
              knows who in the office wrote it. */}
          {!isMine && note.authorName && (
            <Typography
              variant="caption"
              sx={{ color: "info.dark", fontWeight: 500 }}
              noWrap
            >
              · {note.authorName}
            </Typography>
          )}
        </Stack>
        {/* Share toggle only renders for own notes — teammates' notes can
            be SEEN as shared (the chip color + author chip already say
            so) but only the author can change the sharing state. */}
        {onSetOrgShare && isMine && (
          <Tooltip
            title={
              isShared
                ? t("userNotes.sharedWithOrgTooltip")
                : t("userNotes.shareWithOrgTooltip")
            }
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={isShared}
                  onChange={(_, checked) => onSetOrgShare(note.id, checked)}
                  icon={<Group sx={{ fontSize: 14, color: "text.disabled" }} />}
                  checkedIcon={<Group sx={{ fontSize: 14, color: "primary.main" }} />}
                  sx={{ p: 0.25 }}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ color: isShared ? "primary.main" : "text.secondary" }}
                >
                  {isShared ? t("userNotes.sharedWithOrg") : t("userNotes.private")}
                </Typography>
              }
            />
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
}
